import React, { useEffect, useRef } from "react";

/* Lightweight canvas starfield with slow parallax drift.
   Fixed full-screen layer behind all content. */
export default function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h, dpr;
    let stars = [];

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const seed = (() => {
      // deterministic-ish PRNG so layout is stable
      let s = 1337;
      return () => {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        return s / 0x7fffffff;
      };
    })();

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(180, Math.floor((w * h) / 9000));
      stars = Array.from({ length: count }, () => {
        const layer = seed();
        return {
          x: seed() * w,
          y: seed() * h,
          z: 0.3 + layer * 1.4, // size + speed factor
          tw: seed() * Math.PI * 2, // twinkle phase
          hue: seed() > 0.85 ? "var" : "white",
        };
      });
    };

    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.012;
      for (const s of stars) {
        // slow downward + rightward drift, wrap around
        s.y += s.z * 0.12;
        s.x += s.z * 0.03;
        if (s.y > h) { s.y = 0; s.x = seed() * w; }
        if (s.x > w) s.x = 0;

        const twinkle = 0.5 + 0.5 * Math.sin(t * s.z + s.tw);
        const r = s.z * 0.9;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        if (s.hue === "var") {
          ctx.fillStyle = `rgba(52,224,255,${0.35 + twinkle * 0.55})`;
        } else {
          ctx.fillStyle = `rgba(220,238,255,${0.25 + twinkle * 0.5})`;
        }
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    build();
    if (reduce) {
      // single static frame
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.z * 0.9, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(220,238,255,0.5)";
        ctx.fill();
      }
    } else {
      draw();
    }

    const onResize = () => build();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
}
